export const EMPTY_MATRIX_FILTERS = {
    search: '',
    trade: '',
    agency: '',
    expiring: false,
    expired: false,
    missing: false,
    visaExpiring: false,
    recentActive: false,
    recentDays: 30,
};
export const MATRIX_VIEW_LABELS = {
    'all': 'All Workers',
    'deployed-by-project': 'Deployed — by Project',
    'deployed-flat': 'Deployed — Flat',
};
export const MATRIX_OTHER_GROUP = 'OTHER / CUSTOM';
export const MATRIX_CATEGORY_ORDER = ['MANDATORY', 'BENEFICIAL', 'OTHER'];
export const MATRIX_STATUS_TONES = {
    valid: { fill: 'DCFCE7', text: '16A34A' },
    expiring: { fill: 'FEF3C7', text: 'D97706' },
    expired: { fill: 'FEE2E2', text: 'DC2626' },
    empty: { fill: 'F1F3F8', text: '9AA3B5' },
    missing: { fill: 'EF4444', text: 'FFFFFF' },
};
export const MATRIX_CATEGORY_TONES = {
    MANDATORY: { fill: 'FCEFD3', text: '92600F' },
    BENEFICIAL: { fill: 'DBEAFE', text: '1D4ED8' },
    OTHER: { fill: 'EDE9FE', text: '6D28D9' },
};
export const MATRIX_PROJECT_BAND_TONE = { fill: 'FFF2CC', text: '92600F' };
const MONTHS_SHORT_EN_GB = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
function isoOf(d) {
    if (!d)
        return null;
    return typeof d === 'string' ? d : d.toISOString();
}
function parseDate(d) {
    if (!d)
        return null;
    const dt = d instanceof Date ? d : new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt;
}
export function formatMatrixExpiry(d) {
    const iso = isoOf(d);
    if (!iso)
        return '—';
    const dt = parseDate(iso);
    if (!dt)
        return iso.slice(0, 10);
    const day = String(dt.getDate()).padStart(2, '0');
    return `${day}-${MONTHS_SHORT_EN_GB[dt.getMonth()]}-${String(dt.getFullYear()).slice(-2)}`;
}
export function matrixWeekEndingLabel(today = new Date()) {
    const day = today.getDay();
    const diff = (5 - day + 7) % 7;
    const friday = new Date(today);
    friday.setDate(today.getDate() + diff);
    const dd = String(friday.getDate()).padStart(2, '0');
    return `${dd}-${MONTHS_SHORT_EN_GB[friday.getMonth()]}-${friday.getFullYear()}`;
}
export function matrixDaysUntil(d, today = new Date()) {
    const dt = parseDate(d);
    if (!dt)
        return null;
    const base = new Date(today);
    base.setHours(0, 0, 0, 0);
    return Math.floor((dt.getTime() - base.getTime()) / 86400000);
}
export function matrixCscsState(expiry, today = new Date()) {
    const days = matrixDaysUntil(expiry, today);
    if (days === null)
        return 'none';
    if (days < 0)
        return 'expired';
    if (days < 90)
        return 'expiring';
    return 'valid';
}
export function certMatchesColumn(certType, col) {
    const t = certType.trim().toLowerCase();
    return t === col.label.toLowerCase() || t === col.key.toLowerCase();
}
export function fuzzyMatchLegacyRow(workerName, rows) {
    const n = workerName.toLowerCase().trim();
    const parts = n.split(/\s+/);
    const fnL = parts[0] ?? '';
    const lnL = parts[parts.length - 1] ?? '';
    return rows.find((r) => r.fn.toLowerCase() === fnL && r.ln.toLowerCase() === lnL)
        ?? rows.find((r) => r.fn.toLowerCase() === fnL);
}
export function findCscs(w, legacyRow) {
    if (legacyRow?.certs?.['CS'])
        return { expiry: legacyRow.certs['CS'], source: 'mx' };
    const c = w.certs.find((x) => /^cscs\b/i.test(x.type.trim()));
    const iso = isoOf(c?.expiry);
    if (iso)
        return { expiry: iso, source: 'crm' };
    return { expiry: null, source: null };
}
export function buildRequiredForRole(roleReqs) {
    const m = {};
    for (const r of roleReqs)
        m[r.role] = r.mandatory ?? [];
    return m;
}
export function isMissingMandatory(w, requiredForRole) {
    const roles = w.roles?.length ? w.roles : [w.role];
    const held = new Set(w.certs.map((c) => c.type));
    return roles.some((r) => (requiredForRole[r] ?? []).some((t) => !held.has(t)));
}
// Compare cert types loosely so "CSCS" matches "CSCS Green Card", "CSCS Gold",
// etc. — workers often have certs with a more specific type label than the
// role-requirement names. Without this fuzzy match the missing-mandatory
// banner sticks even after the user records a renewal cert.
export function certTypeMatchesLoose(a, b) {
    const norm = (s) => s.toLowerCase().trim().replace(/\s+/g, ' ');
    const A = norm(a);
    const B = norm(b);
    if (!A || !B)
        return false;
    return A === B || A.includes(B) || B.includes(A);
}
export function listMissingMandatory(w, requiredForRole) {
    const roles = w.roles?.length ? w.roles : [w.role];
    const required = [];
    for (const r of roles) {
        for (const t of requiredForRole[r] ?? []) {
            if (!required.includes(t))
                required.push(t);
        }
    }
    return required.filter((ct) => !w.certs.some((c) => certTypeMatchesLoose(c.type, ct)));
}
export function matrixRtwExpired(w, today = new Date()) {
    if (w.rtwStatus !== 'visa' || !w.rtwExpiry)
        return false;
    const days = matrixDaysUntil(w.rtwExpiry, today);
    return days !== null && days < 0;
}
export function matrixRtwExpiring(w, today = new Date(), withinDays = 60) {
    if (w.rtwStatus !== 'visa' || !w.rtwExpiry)
        return false;
    const days = matrixDaysUntil(w.rtwExpiry, today);
    return days !== null && days >= 0 && days < withinDays;
}
export function matrixRtwLabel(w, today = new Date()) {
    if (!w.rtwStatus)
        return '';
    if (w.rtwStatus === 'visa' && w.rtwExpiry) {
        const iso = isoOf(w.rtwExpiry);
        const days = matrixDaysUntil(w.rtwExpiry, today);
        const tag = days !== null && days < 0 ? 'EXPIRED' : days !== null && days < 60 ? 'EXPIRING' : 'VISA';
        return `${tag} ${iso.slice(0, 10)}`;
    }
    return w.rtwStatus.toUpperCase();
}
export function isRecentCert(addedAt, days, today = new Date()) {
    const d = parseDate(addedAt);
    if (!d)
        return false;
    return (today.getTime() - d.getTime()) / 86400000 <= days;
}
export function certTypeToColumn(t) {
    return {
        key: t.shortKey ?? `CT_${t.id}`,
        label: t.value,
        group: t.groupName ?? MATRIX_OTHER_GROUP,
    };
}
// Skip rows in the STICKY group — those are surfaced as sticky columns (CSCS), not matrix cells.
export function selectTrainColumns(allCols) {
    return allCols.filter((c) => c.group !== 'STICKY' && c.key !== 'CS');
}
export function buildCategoryGroups(trainCols, ctx) {
    const reqs = ctx.trade ? ctx.roleRequirements.filter((r) => r.role === ctx.trade) : ctx.roleRequirements;
    const mandatoryNames = reqs.flatMap((r) => r.mandatory ?? []);
    const beneficialNames = reqs.flatMap((r) => r.beneficial ?? []);
    const otherNames = reqs.flatMap((r) => r.other ?? []);
    const tradeWorkers = ctx.trade ? ctx.workers.filter((w) => w.role === ctx.trade) : [];
    const inList = (names, col) => names.some((n) => certMatchesColumn(n, col));
    const buckets = { MANDATORY: [], BENEFICIAL: [], OTHER: [] };
    for (const col of trainCols) {
        if (inList(mandatoryNames, col))
            buckets.MANDATORY.push(col);
        else if (inList(beneficialNames, col))
            buckets.BENEFICIAL.push(col);
        else if (!ctx.trade)
            buckets.OTHER.push(col);
        else if (inList(otherNames, col) || tradeWorkers.some((w) => w.certs.some((c) => certMatchesColumn(c.type, col)))) {
            buckets.OTHER.push(col);
        }
    }
    return MATRIX_CATEGORY_ORDER
        .map((label) => ({ label, cols: buckets[label] }))
        .filter((g) => g.cols.length > 0);
}
export function columnMandatoryForWorker(w, col, requiredForRole) {
    const roles = w.roles?.length ? w.roles : [w.role];
    return roles.some((r) => (requiredForRole[r] ?? []).some((n) => certMatchesColumn(n, col)));
}
export function activeDeploymentIndex(deployments) {
    const active = deployments.filter((d) => d.status === 'active');
    const deployedWorkerIds = new Set(active.map((d) => d.workerId));
    const workerProject = {};
    for (const d of active) {
        if (!workerProject[d.workerId])
            workerProject[d.workerId] = { id: d.projectId, name: d.projectName ?? d.projectId };
    }
    return { deployedWorkerIds, workerProject };
}
export function filterMatrixWorkers(workers, descriptor, ctx) {
    const today = ctx.today ?? new Date();
    const f = descriptor.filters;
    let r = [...workers];
    const s = f.search.toLowerCase().trim();
    if (s)
        r = r.filter((w) => w.name.toLowerCase().includes(s));
    if (f.trade)
        r = r.filter((w) => w.role === f.trade);
    if (f.agency)
        r = r.filter((w) => w.agencyCode === f.agency);
    if (f.expiring)
        r = r.filter((w) => w.certs.some((c) => c.status === 'expiring'));
    if (f.expired)
        r = r.filter((w) => w.certs.some((c) => c.status === 'expired'));
    if (f.missing)
        r = r.filter((w) => isMissingMandatory(w, ctx.requiredForRole));
    if (f.visaExpiring)
        r = r.filter((w) => matrixRtwExpired(w, today) || matrixRtwExpiring(w, today, 60));
    if (f.recentActive)
        r = r.filter((w) => w.certs.some((c) => isRecentCert(c.addedAt, f.recentDays, today)));
    if (descriptor.view === 'deployed-flat' || descriptor.view === 'deployed-by-project') {
        r = r.filter((w) => ctx.deployedWorkerIds.has(w.id));
    }
    return r;
}
export function sortMatrixWorkers(rows, sortBy, sortDir, trainCols) {
    const col = sortBy === 'name' ? null : trainCols.find((c) => c.key === sortBy) ?? null;
    const r = [...rows];
    r.sort((a, b) => {
        let av;
        let bv;
        if (!col) {
            av = a.name.toLowerCase();
            bv = b.name.toLowerCase();
        }
        else {
            const ac = a.certs.find((c) => certMatchesColumn(c.type, col));
            const bc = b.certs.find((c) => certMatchesColumn(c.type, col));
            const at = parseDate(ac?.expiry);
            const bt = parseDate(bc?.expiry);
            av = at ? at.getTime() : Number.MAX_SAFE_INTEGER;
            bv = bt ? bt.getTime() : Number.MAX_SAFE_INTEGER;
        }
        if (av < bv)
            return sortDir === 'asc' ? -1 : 1;
        if (av > bv)
            return sortDir === 'asc' ? 1 : -1;
        return 0;
    });
    return r;
}
export function groupRowsByProject(rows, workerProject) {
    const groups = {};
    for (const w of rows) {
        const proj = workerProject[w.id];
        if (!proj)
            continue;
        if (!groups[proj.id])
            groups[proj.id] = { projectId: proj.id, projectName: proj.name, workers: [] };
        groups[proj.id].workers.push(w);
    }
    return Object.values(groups).sort((a, b) => a.projectName.localeCompare(b.projectName));
}
export function computeColStats(rows, trainCols, today = new Date()) {
    const total = rows.length;
    const stats = {};
    for (const c of trainCols) {
        let held = 0;
        for (const w of rows) {
            const cert = w.certs.find((x) => certMatchesColumn(x.type, c));
            const days = matrixDaysUntil(cert?.expiry, today);
            if (days !== null && days >= 0)
                held++;
        }
        stats[c.key] = { held, total, pct: total ? Math.round((held / total) * 100) : 0 };
    }
    return stats;
}
export function computeMatrixKpis(workers, requiredForRole, today = new Date()) {
    const allCerts = workers.flatMap((w) => w.certs);
    const recentSet = new Set();
    let recentCertCount = 0;
    for (const w of workers) {
        const recent = w.certs.filter((c) => isRecentCert(c.addedAt, 30, today));
        if (recent.length) {
            recentSet.add(w.name.toLowerCase());
            recentCertCount += recent.length;
        }
    }
    return {
        totalCerts: allCerts.length,
        validCerts: allCerts.filter((c) => c.status === 'valid').length,
        expiringCerts: allCerts.filter((c) => c.status === 'expiring').length,
        expiredCerts: allCerts.filter((c) => c.status === 'expired').length,
        missingCount: workers.filter((w) => isMissingMandatory(w, requiredForRole)).length,
        recentWorkerCount: recentSet.size,
        recentCertCount,
        visaExpiring: workers.filter((w) => matrixRtwExpired(w, today) || matrixRtwExpiring(w, today, 60)).length,
        visaExpired: workers.filter((w) => matrixRtwExpired(w, today)).length,
    };
}
export function matrixFilterSummary(f) {
    const parts = [];
    if (f.search.trim())
        parts.push(`Search "${f.search.trim()}"`);
    if (f.trade)
        parts.push(`Trade: ${f.trade}`);
    if (f.agency)
        parts.push(`Agency: ${f.agency}`);
    if (f.expiring)
        parts.push('Expiring <90d');
    if (f.expired)
        parts.push('Expired');
    if (f.missing)
        parts.push('Missing mandatory');
    if (f.visaExpiring)
        parts.push('Visa expiring <60d');
    if (f.recentActive)
        parts.push(`Added <${f.recentDays}d`);
    return parts.length ? parts.join(' · ') : 'None';
}
function toRowModel(w, columns, legacyRows, requiredForRole, today) {
    const legacy = fuzzyMatchLegacyRow(w.name, legacyRows);
    const { expiry: cscs } = findCscs(w, legacy);
    return {
        workerId: w.id,
        name: w.name,
        core: w.core,
        rating: w.rating,
        trade: w.role,
        agency: w.agencyCode ?? legacy?.ag ?? '',
        cscsExpiry: cscs,
        cscsState: matrixCscsState(cscs, today),
        rtwLabel: matrixRtwLabel(w, today),
        cells: columns.map((c) => {
            const cert = w.certs.find((x) => certMatchesColumn(x.type, c));
            const expiry = isoOf(cert?.expiry);
            const status = cert?.status
                ?? (columnMandatoryForWorker(w, c, requiredForRole) ? 'missing' : 'empty');
            return {
                key: c.key,
                label: c.label,
                status,
                expiry,
                expiryLabel: expiry ? formatMatrixExpiry(expiry) : status === 'missing' ? 'MISSING' : '—',
            };
        }),
    };
}
export function buildTrainingMatrixModel(inputs, descriptor, today = new Date()) {
    const trainCols = selectTrainColumns(inputs.certColumns);
    const columnGroups = buildCategoryGroups(trainCols, {
        roleRequirements: inputs.roleRequirements,
        trade: descriptor.filters.trade,
        workers: inputs.workers,
    });
    const columns = columnGroups.flatMap((g) => g.cols);
    const requiredForRole = buildRequiredForRole(inputs.roleRequirements);
    const { deployedWorkerIds, workerProject } = activeDeploymentIndex(inputs.deployments);
    const filtered = filterMatrixWorkers(inputs.workers, descriptor, { deployedWorkerIds, requiredForRole, today });
    const sorted = sortMatrixWorkers(filtered, descriptor.sortBy, descriptor.sortDir, columns);
    const rowOf = (w) => toRowModel(w, columns, inputs.legacyRows, requiredForRole, today);
    const groups = descriptor.view === 'deployed-by-project'
        ? groupRowsByProject(sorted, workerProject).map((g) => ({ projectId: g.projectId, projectName: g.projectName, rows: g.workers.map(rowOf) }))
        : [{ projectId: null, projectName: null, rows: sorted.map(rowOf) }];
    const filterSummary = matrixFilterSummary(descriptor.filters);
    return {
        view: descriptor.view,
        viewLabel: MATRIX_VIEW_LABELS[descriptor.view],
        weekEnding: matrixWeekEndingLabel(today),
        filterSummary,
        filtered: filterSummary !== 'None',
        totalWorkers: inputs.workers.length,
        visibleCount: sorted.length,
        columnGroups,
        columns,
        groups,
        colStats: computeColStats(sorted, columns, today),
        kpis: computeMatrixKpis(inputs.workers, requiredForRole, today),
    };
}
//# sourceMappingURL=training-matrix.js.map